import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ClipboardList, FileInput, Layout, Clapperboard, CheckCircle2, ArrowRight } from 'lucide-react';
interface SlideGetStartedProps {
  onContinue: () => void;
}
interface Step {
  icon: React.ReactNode;
  label: string;
  items: string[];
  micro: string;
}
export function SlideGetStarted({
  onContinue
}: SlideGetStartedProps) {
  const [activeStep, setActiveStep] = useState(-1);
  const [showInsight, setShowInsight] = useState(false);
  const steps: Step[] = [{
    icon: <ClipboardList className="w-4 h-4" data-id="element-122" />,
    label: 'Brief',
    items: ['Brand, markets, objectives', 'CTA & use cases'],
    micro: 'You define the goal'
  }, {
    icon: <FileInput className="w-4 h-4" data-id="element-123" />,
    label: 'Content Input',
    items: ['Slides, PDFs, data', 'CSA, trial data, videos'],
    micro: 'Anything relevant — we take it all'
  }, {
    icon: <Layout className="w-4 h-4" data-id="element-124" />,
    label: 'Storyboard & Concept',
    items: ['Snak journey', 'Hook for channels'],
    micro: 'Visual + interactive from day one'
  }, {
    icon: <Clapperboard className="w-4 h-4" data-id="element-125" />,
    label: 'Production & Setup',
    items: ['Content, video, animation', 'Medical copy (first draft)', 'Channel setup'],
    micro: ''
  }, {
    icon: <CheckCircle2 className="w-4 h-4" data-id="element-126" />,
    label: 'Approved & Ready',
    items: ['Design + MLR approved', 'Campaign ready to deploy'],
    micro: 'Brief to live in weeks — not months'
  }];
  useEffect(() => {
    const timers = steps.map((_, idx) => setTimeout(() => setActiveStep(idx), 600 + idx * 700));
    const insightTimer = setTimeout(() => setShowInsight(true), 600 + steps.length * 700 + 400);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(insightTimer);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-14 md:py-12 lg:px-20 lg:py-14 max-w-7xl mx-auto" data-id="element-127">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-white mb-8 text-center" data-id="element-128">
        What happens <span className="text-teal-400" data-id="element-129">when we start?</span>
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-id="element-130">
        {/* Steps Flow */}
        <div className="lg:col-span-8 relative" data-id="element-131">
          {/* Connection line */}
          <div className="absolute top-10 left-[10%] right-[10%] h-[2px] bg-navy-600 z-0" data-id="element-132">
            <motion.div className="h-full bg-teal-400/50" initial={{
            width: '0%'
          }} animate={{
            width: activeStep >= 4 ? '100%' : `${(activeStep + 1) / 5 * 100}%`
          }} transition={{
            duration: 0.4
          }} data-id="element-133" />
          </div>

          <div className="grid grid-cols-5 gap-2 relative z-10" data-id="element-134">
            {steps.map((step, idx) => {
            const isActive = idx <= activeStep;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: isActive ? 1 : 0.2,
              y: isActive ? 0 : 15
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center text-center" data-id="element-135">
                  {/* Icon */}
                  <motion.div animate={{
                backgroundColor: isActive ? '#00D4AA' : '#1E2D4A',
                scale: isActive ? 1 : 0.8
              }} className="w-8 h-8 rounded-full flex items-center justify-center text-navy-900 mb-3 shadow-md" data-id="element-136">
                    {step.icon}
                  </motion.div>

                  {/* Card */}
                  <div className={`rounded-xl p-3 w-full transition-all duration-400 ${isActive ? 'bg-navy-700/60 border border-navy-500' : 'bg-navy-800/30 border border-navy-700/30'}`} data-id="element-137">
                    <h4 className={`font-semibold text-xs mb-2 ${isActive ? 'text-teal-400' : 'text-white/30'}`} data-id="element-138">
                      {step.label}
                    </h4>

                    <div className="space-y-1 mb-2" data-id="element-139">
                      {step.items.map((item, i) => <div key={i} className={`text-xs flex items-start gap-1 ${isActive ? 'text-white/80' : 'text-white/20'}`} data-id="element-140">
                          <div className={`w-1 h-1 rounded-full flex-shrink-0 mt-1.5 ${isActive ? 'bg-teal-400' : 'bg-navy-600'}`} data-id="element-141" />
                          {item}
                        </div>)}
                    </div>

                    {step.micro && <p className={`text-xs italic border-t pt-1.5 ${isActive ? 'text-teal-400/70 border-navy-500' : 'text-white/15 border-navy-700'}`} data-id="element-142">
                        {step.micro}
                      </p>}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Insight Box */}
        <div className="lg:col-span-4" data-id="element-143">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: showInsight ? 1 : 0,
          x: showInsight ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-navy-700/50 border border-navy-600 p-6 rounded-2xl" data-id="element-144">
            <div className="text-teal-400 font-medium mb-4 flex items-center gap-2 text-sm" data-id="element-145">
              <CheckCircle2 className="w-4 h-4" data-id="element-146" />
              Insight
            </div>
            <div className="text-lg leading-relaxed text-white/90 space-y-3" data-id="element-147">
              <p className="font-medium" data-id="element-148">You don't build assets.</p>
              <p className="text-white/70" data-id="element-149">
                <span className="text-teal-400 mr-1" data-id="element-150">→</span>
                You launch a ready-to-run campaign
              </p>
            </div>

            <div className="mt-6" data-id="element-151">
              <Button onClick={onContinue} data-id="element-152">What happens after launch</Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}